<script setup>
import { WdsIcon, WdsPagination, WdsPropertyCard, WdsTag } from '@wds/components';
import cityConfig from '@/config/city';
import { findPropertyTypeByKey } from '@/utils/propertyTypes';
import { getThreePiCategoryTheme, getThreePiTypeLabel } from '@/utils/threePiPropertyLabel';

const props = defineProps({
  propertyList: {
    type: Array,
    required: true,
  },
  featuredProperties: {
    type: Array,
    required: false,
    default: () => [],
  },
  totalPages: {
    type: Number,
    required: false,
    default: 0,
  },
  currentPage: {
    type: Number,
    required: false,
    default: 0,
  },
  propertyTypeObj: {
    type: Object,
    required: true,
  },
  locationInfo: {
    type: Object,
    required: true,
  },
  activeFilter: {
    type: Object,
    default: () => ({}),
  },
});

const emit = defineEmits([
  'highlightMapMarker',
  'sustainabilityPinClick',
  'linkupsEventsClick',
  'whosStayingClick',
  'thirdPartyPropertyClick',
]);

const { t } = useI18n();
const { useImage } = useImages();
const { useUrl } = useUrls();
const { useRating } = useRatings();
const { socialCues } = useSocialCues();
const { useFormatter } = useFormatters();
const { getSustainabilityLevel } = useSustainability();
const {
  $tracking,
  $url,
} = useNuxtApp();
const { isBelowMediumScreen } = useMq();
const { appendThreePiQueryParams } = useThreePiQueryParams();

// COD-877: the 3PI q token reads cookies/local storage, so hrefs get it appended after mount.
const isMounted = ref(false);
onMounted(() => {
  isMounted.value = true;
});

// ## STORES
const testingStore = useTestingStore();
const linkupsEnabled = testingStore.hasEnabledFeature('web_linkupsCallouts');
const socialCuesEnabled = testingStore.hasEnabledFeature(
  'web_socialCuesCallouts',
);
const newSortOrderMessage = testingStore.hasEnabledFeature(
  'web_SRPNewSortingMessage',
);

function getPropertyCardLink(property) {
  if (property && property?.type === '3PI') {
    const externalUrl = useUrl.get3PIPropertyPageUrl(property?.urlPath);
    return isMounted.value
      ? appendThreePiQueryParams(externalUrl, property, props.locationInfo)
      : externalUrl;
  }
  const propertyTypeInfo = findPropertyTypeByKey(property.type);
  return useUrl.getPropertyPageUrl({
    propertyTypeSlug: t(propertyTypeInfo.slug),
    propertyId: property.id,
    urlFriendlyProperty: property.urlFriendlyName,
  });
}
function onFeaturePropertyCardClick(property) {
  const trackingInfo = {
    segment: {
      event_name: 'Destination Landing Page Event',
      action: 'Featured Listing Property Clicked',
      destination_continent: property?.tracking?.continent,
      destination_country: property?.tracking?.country,
      destination_city: property?.tracking?.city,
      destination_city_id: props?.locationInfo?.id?.toString(),
    },
  };

  $tracking.onFeaturedPropertyCardClick(trackingInfo.segment);
}
function getCustomPageUrl(pageNumber) {
  if (pageNumber > props.totalPages || pageNumber < 1) {
    return;
  }

  $url.setHref(buildPageUrl(pageNumber));
}
function buildPageUrl(pageNumber) {
  const pageParams = {
    propertyTypeSlug: props.propertyTypeObj.slug,
    urlFriendlyContinent: props.locationInfo.urlFriendlyContinent,
    urlFriendlyCountry: props.locationInfo.urlFriendlyCountry,
    urlFriendlyCity: props.locationInfo.urlFriendlyCity,
  };

  if (props.activeFilter?.slug) {
    pageParams.urlFilterSlug = t(props.activeFilter?.slug);
  }
  if (props.locationInfo.cityId) {
    pageParams.urlFriendlyDistrict = props.locationInfo.urlFriendlyDistrict;
    return useUrl.getDistrictPageUrl(pageParams, {}, pageNumber);
  }

  return useUrl.getCityPageUrl(pageParams, {}, pageNumber);
}
function handleCardHover(id) {
  emit('highlightMapMarker', id);
}
function handleSustainabilityPinClick(propertySustainabilityInfo) {
  emit(
    'sustainabilityPinClick',
    getSustainabilityLevel(propertySustainabilityInfo),
  );
}
function linkupsEventsClick(id, linkupsCount) {
  emit('linkupsEventsClick', {
    id,
    linkupsCount,
  });
}

function handleSocialCuesClick(id, socialCuesCount) {
  emit('whosStayingClick', {
    id,
    socialCuesCount,
  });
}

// COD-877: same-tab navigation (product decision) unloads the page before the Segment click
// event resolves, so hold the navigation briefly (same pattern as analytics.js trackLink).
const OUTBOUND_TRACKING_DELAY_MS = 300;

let isNavigationPending = false;

// COD-877: Third Party Property Clicked tracking, handled by the page which owns the 3PI context.
function handlePropertyCardClick(propertyInfo, event) {
  if (propertyInfo?.property?.type !== '3PI' || isNavigationPending) {
    return;
  }
  emit('thirdPartyPropertyClick', {
    property: propertyInfo.property,
    position: propertyInfo.position,
  });

  // Modified clicks (ctrl/cmd/shift) open a new tab natively, so the page — and the
  // in-flight tracking request — survive without intercepting the navigation.
  const href = propertyInfo?.bindProps?.href;
  if (!href || event?.metaKey || event?.ctrlKey || event?.shiftKey) {
    return;
  }
  event.preventDefault();
  isNavigationPending = true;
  setTimeout(() => $url.setHref(href), OUTBOUND_TRACKING_DELAY_MS);
}

const getPropertyListForCards = computed(() => {
  return props.propertyList.map((property, index) => {
    const propertyTypeInfo = findPropertyTypeByKey(property.type);
    const hasPrivateRooms
      = property.hasAvailability && property.privateMinPrice?.value;
    const hasDormRooms
      = property.hasAvailability && property.sharedMinPrice?.value;

    const propertyImages
      = property?.images?.reduce((prevImages, image) => {
        if (image?.small) {
          prevImages.push(useImage.getImage(image.medium));
        }
        return prevImages;
      }, []) || [];

    const propertyBadges
      = property?.badges?.map((badge) => {
        let title = t(`t_${badge.transCode}`);
        let description = null;
        if (badge.transCode === 'SANITISATIONBADGE') {
          description = t('t_HEALTHSANITATIONTOOLTIP');
        }
        if (badge.id === 'hoscar') {
          title = t('t_YEARHOSCARWINNER', { YEAR: badge.year });
          description = `${t(`t_HOSCARSPLACE${badge.place}`)} - ${t(`t_${badge.transCode}`)}`;
        }
        return {
          icon: badge.icon,
          label: badge.showLabel ? title : null,
          ariaLabel: title,
          ariaDescription: description,
          tooltip: {
            heading: title,
            subHeading: description,
            icon: badge.icon,
          },
        };
      }) || [];

    const formatLinkupsToEvents = (linkups) => {
      return linkups?.imageUrls?.reduce(
        (events, url) => {
          events.eventsList.push({ picture: useImage.getOptimizedImageUrl(url, { width: 32 }) });
          return events;
        },
        {
          eventsList: [],
          eventsLimit: 3,
          eventsDescription: linkups?.linkupsCount
            ? t('t_LINKUPCOUNTEVENTS', { NUMEVENTS: linkups?.linkupsCount })
            : '',
          size: 'small',
        },
      );
    };

    const propertySustainability = getSustainabilityLevel(
      property.sustainability,
    );

    const propertySocialCues = {
      avatarList: property?.whosGoing?.users || [],
      totalBookedUsers: socialCues.getSocialCuesLabel(property?.whosGoing?.total),
      avatarLimit: cityConfig.socialCues.limitAvatars,
      size: 'small',
    };

    const actualMinPrivatePrice = {
      curPrice: hasPrivateRooms
        ? useFormatter.formatCurrency(
          property.privateMinPrice.value,
          property.privateMinPrice.currency,
        )
        : null,
    };

    const actualMinDormPrice = {
      curPrice: hasDormRooms
        ? useFormatter.formatCurrency(
          property.sharedMinPrice.value,
          property.sharedMinPrice.currency,
        )
        : null,
    };

    return {
      id: property.id,
      property,
      position: index + 1,
      linkupsCount: property.linkups?.linkupsCount || 0,
      socialCuesCount: property?.whosGoing?.total || 0,
      sustainability: property.sustainability,
      bindProps: {
        type:
          property?.type === '3PI'
            ? getThreePiTypeLabel(property, t)
            : t(propertyTypeInfo.transcode_s),
        typeTheme:
          property?.type === '3PI'
            ? getThreePiCategoryTheme(property.propertyCategory)
            : propertyTypeInfo?.theme,
        name: property.name,
        href: getPropertyCardLink(property),
        description: property?.shortIntro || property?.intro,
        featured: property?.featured || false,
        gallery: {
          slides: propertyImages,
          alt: property.name,
          title: property.name,
        },
        rating: Number(property.avgRating) > 0
          ? {
            score: property.avgRating,
            totalReviews: property.numberReviews,
            translations: toValue(useRating.getRatingScoreKeywordTranslations),
          }
          : null,
        distance: t('t_FROMCITYCENTER', { DISTANCE: property.distance }),
        badges: propertyBadges,
        minPrivatePrice: actualMinPrivatePrice,
        minDormPrice: actualMinDormPrice,
        showPricing: property?.type !== '3PI',
        footerMessage: property?.type === '3PI'
          ? {
            asset: 'calendar',
            label: t('t_PROPERTYCARDADDDATESTOSEEPRICES'),
          }
          : null,
        sustainabilityLevel: null,
        sustainabilityBadge: Boolean(propertySustainability),
        linkupsEvents:
          linkupsEnabled && property.linkups
            ? formatLinkupsToEvents(property.linkups)
            : {},
        stayingAvatars:
          socialCuesEnabled && property?.whosGoing?.total
            ? propertySocialCues
            : {},
      },
    };
  });
});
const getPropertyCardTranslations = computed(() => {
  return {
    tPrivatesFrom: t('t_PRIVATESFROM'),
    tDormsFrom: t('t_DORMSFROM'),
    tNoPrivatesAvailable: t('t_NOPRIVATESAVAILABLE'),
    tNoDormsAvailable: t('t_NODORMSAVAILABLE'),
    tNoAvailability: t('t_NOAVAILABILITY'),
    tPricesFrom: t('t_PRICESFROM'),
  };
});

const getFeaturedPropertyListForCards = computed(() => {
  const featuredPropertyList = props.featuredProperties?.slice(0, 3);
  return featuredPropertyList.map((property) => {
    const privateRoomsPrice = property.privateMinPrice?.value || 0;
    const dormRoomsPrice = property.sharedMinPrice?.value || 0;
    const pricesWithoutZeros = [privateRoomsPrice, dormRoomsPrice]
      .filter((n) => Number(n) > 0)
      .map(Number);
    const propertyMinPrice = pricesWithoutZeros.length
      ? Math.min.apply(0, pricesWithoutZeros)
      : null;
    property.minPriceString = propertyMinPrice
      ? useFormatter.formatCurrency(
        propertyMinPrice,
        property.sharedMinPrice.currency,
      )
      : '';

    const propertyImage = useImage.getImage(property?.image?.small) || null;

    return {
      id: property.id,
      tracking: property.tracking,
      bindProps: {
        name: property.name,
        href: getPropertyCardLink(property),
        gallery: {
          slides: [propertyImage],
          alt: property.name,
          title: property.name,
        },
        rating: Number(property.avgRating) > 0
          ? {
            size: 'small',
            score: property.avgRating,
            totalReviews: property.numberReviews,
            translations: toValue(useRating.getRatingScoreKeywordTranslations),
          }
          : null,
        fromPrice: propertyMinPrice
          ? { curPrice: property.minPriceString }
          : null,
        translations: getPropertyCardTranslations.value,
      },
    };
  });
});
const getPrevPageUrl = computed(() => {
  if (props.currentPage <= 1) {
    return null;
  }
  return buildPageUrl(props.currentPage - 1);
});
const getNextPageUrl = computed(() => {
  if (props.currentPage >= props.totalPages) {
    return null;
  }
  return buildPageUrl(props.currentPage + 1);
});
const hasFeaturedProperties = computed(() => {
  return props.featuredProperties?.length;
});
</script>

<template>
  <div
    v-if="getPropertyListForCards.length > 0"
    class="property-listing-section"
  >
    <div v-if="hasFeaturedProperties" class="featured-listing-container">
      <span class="featured-listing-title">{{
        $t("t_FEATUREDPROPERTIES")
      }}</span>
      <div class="featured-listing-wrapper">
        <div class="featured-listing-cards">
          <WdsPropertyCard
            v-for="featPropertyInfo in getFeaturedPropertyListForCards"
            :key="`f${featPropertyInfo.id}`"
            class="featured-property-card"
            layout="compact"
            orientation="horizontal"
            v-bind="featPropertyInfo.bindProps"
            @click="onFeaturePropertyCardClick(featPropertyInfo)"
            @keyup.enter="onFeaturePropertyCardClick(featPropertyInfo)"
          />
        </div>
      </div>
    </div>
    <div class="property-listing-sort-order-message">
      <WdsIcon asset="info" color="ink" />
      <div v-if="newSortOrderMessage">
        <span>{{ $t("t_SORTORDERCMANEW") }}&nbsp;</span>
        <a
          :href="useUrl.getGenericPageUrl('legal/transparency-in-ranking')"
          :title="$t('t_READMORE')"
          :aria-label="$t('t_READMORE')"
          target="_blank"
        >{{ $t("t_READMORE") }}</a>
      </div>
      <span v-else>{{ $t("t_RWDSORTEDBYOURALGORITHM") }}</span>
    </div>
    <div class="property-listing-cards">
      <WdsPropertyCard
        v-for="propertyInfo in getPropertyListForCards"
        :key="`p${propertyInfo.id}`"
        class="property-listing-card"
        :data-pkey="`p${propertyInfo.id}`"
        :orientation="isBelowMediumScreen ? 'vertical' : 'horizontal'"
        v-bind="propertyInfo.bindProps"
        :translations="getPropertyCardTranslations"
        @click="handlePropertyCardClick(propertyInfo, $event)"
        @mouseenter.self="handleCardHover(propertyInfo.id)"
        @mouseleave.self="handleCardHover(0)"
        @focusin.self="handleCardHover(propertyInfo.id)"
        @focusout.self="handleCardHover(0)"
        @sustainability-pin-click="
          handleSustainabilityPinClick(propertyInfo.sustainability)
        "
        @linkups-events-click="
          linkupsEventsClick(propertyInfo.id, propertyInfo.linkupsCount)
        "
        @whos-staying-click="
          handleSocialCuesClick(propertyInfo.id, propertyInfo.socialCuesCount)
        "
      >
        <template #custom-tags>
          <WdsTag
            v-if="propertyInfo.bindProps.featured"
            :text="$t('t_FEATUREDPROPERTY')"
            color="purple"
          />
        </template>
      </WdsPropertyCard>
      <WdsPagination
        v-if="totalPages"
        class="property-listing-pagination"
        :num-pages="totalPages"
        :starting-page="currentPage"
        :next-page-url="getNextPageUrl"
        :prev-page-url="getPrevPageUrl"
        @page-move="getCustomPageUrl"
      />
    </div>
  </div>
</template>

<style lang="scss" scoped>
.property-listing-section {
  display: flex;
  flex-direction: column;
  gap: $wds-spacing-m;
  min-width: 100%;

  :deep(a) {
    fill: $wds-color-ink-darker;
  }

  .featured-listing-container {
    display: flex;
    flex-direction: column;
    border-radius: $wds-border-radius-xl;
    background-color: $wds-color-ink-lightest;

    .featured-listing-title {
      @include body-1-reg;

      margin: $wds-spacing-m $wds-spacing-m 0 $wds-spacing-m;
    }

    .featured-listing-wrapper {
      overflow: hidden;
      border-radius: $wds-border-radius-xl;
      display: flex;
    }

    .featured-listing-cards {
      display: flex;
      gap: $wds-spacing-s;
      overflow-x: auto;
      padding: $wds-spacing-m;
      flex: 1;

      .featured-property-card {
        flex: 0 0 wds-rem(328px);
      }
    }
  }

  .property-listing-sort-order-message {
    display: inline-flex;
    margin: 0;
    gap: $wds-spacing-xs;
    color: $wds-color-ink;

    a {
      color: $wds-color-ink;
      text-decoration: underline;
    }

    @include body-3-reg;
  }

  .property-listing-cards {
    display: flex;
    flex-direction: column;
    gap: $wds-spacing-m;
    width: 100%;

    .property-listing-pagination {
      justify-content: center;
    }
  }

  @include desktop {
    .property-listing-cards {
      flex: 1;
    }
  }
}
</style>
